/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DataTable } from '@/components/CustomTable'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Pencil, Trash2 } from 'lucide-react'
import { useDebounce } from '@/hooks/useDebounce'
import {
  useCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
} from '@/hooks/useCategories'
import type { Category } from '@/types/backend'
import { toast } from 'sonner'
import { Label } from '@/components/ui/label'
import type { ColumnDef } from '@tanstack/react-table'
import AppDialog, { ModalActions } from '@/components/CustomModal'
import Loading from '@/components/Loading'
import { useTableUrlState } from '@/hooks/useTableUrlState'

export default function CategoriesPage() {
  const navigate = useNavigate()

  const [open, setOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<{
    id: string
    name?: string
    itemsCount?: number
  } | null>(null)
  const [detach, setDetach] = useState(false)

  const [editing, setEditing] = useState<Category | null>(null)
  const [form, setForm] = useState<{
    name: string
    slug: string
    vatRate: string
  }>({ name: '', slug: '', vatRate: '' })

  const {
    state: { search, pageIndex, pageSize },
    setSearch,
    setPageIndex,
    setPageSize,
  } = useTableUrlState('categories', { search: '', pageIndex: 0, pageSize: 20 })
  const debounced = useDebounce(search, 400)

  const {
    data: list,
    status,
    isFetching,
  } = useCategoriesQuery(
    { search: debounced || undefined, page: pageIndex + 1, pageSize },
    true
  )

  const tableProps = {
    searchValue: search,
    onSearchChange: setSearch,
    currentPageIndex: pageIndex,
    currentPageSize: pageSize,
    onPageChange: setPageIndex,
    onPageSizeChange: setPageSize,
    paginationServer: true as const,
    sortingServer: false as const,
  }

  const total = list?.total ?? 0
  const items = list?.data ?? []
  const pageCount = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  )

  const createMut = useCreateCategoryMutation()
  const updateMut = useUpdateCategoryMutation(editing?.id || '')
  const deleteMut = useDeleteCategoryMutation()

  const columns = useMemo<ColumnDef<Category>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Nom',
        cell: ({ row }) => (
          <button
            onClick={(e) => {
              e.preventDefault()
              navigate(
                `/items?categoryId=${
                  row.original.id
                }&category=${encodeURIComponent(row.original.name)}`
              )
            }}
            className='font-medium underline-offset-2 hover:underline'
          >
            {row.original.name}
          </button>
        ),
      },
      {
        accessorKey: 'slug',
        header: 'Slug',
        cell: ({ row }) => row.original.slug || '—',
      },
      {
        accessorKey: 'vatRate',
        header: 'TVA',
        cell: ({ row }) =>
          row.original.vatRate != null ? `${row.original.vatRate}%` : '—',
      },
      {
        accessorKey: 'itemsCount',
        header: 'Articles',
        cell: ({ row }) => row.original.itemsCount ?? 0,
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className='flex items-center gap-3'>
            <Pencil
              size={18}
              className='cursor-pointer text-green-600 mr-4'
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                setEditing(row.original)
                setForm({
                  name: row.original.name ?? '',
                  slug: row.original.slug ?? '',
                  vatRate:
                    row.original.vatRate != null
                      ? String(row.original.vatRate)
                      : '',
                })
                setOpen(true)
              }}
            />
            <Trash2
              size={18}
              className='cursor-pointer text-red-600 ml-4'
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                setPendingDelete({
                  id: row.original.id,
                  name: row.original.name,
                  itemsCount: row.original.itemsCount,
                })
                setDetach(false)
                setConfirmOpen(true)
              }}
            />
          </div>
        ),
      },
    ],
    [navigate]
  )

  const openCreate = () => {
    setEditing(null)
    setForm({ name: '', slug: '', vatRate: '' })
    setOpen(true)
  }

  const submit = async () => {
    const name = form.name.trim()
    const slug = form.slug.trim()
    const vatRate =
      form.vatRate.trim() === ''
        ? undefined
        : Number(form.vatRate.replace(',', '.'))

    if (!name) {
      toast.error('Nom requis')
      return
    }
    if (vatRate != null && (isNaN(vatRate) || vatRate < 0 || vatRate > 100)) {
      toast.error('TVA invalide', { description: 'Valeur entre 0 et 100' })
      return
    }

    try {
      if (editing) {
        // En édition, null = vider le champ TVA ; undefined le laisserait
        // inchangé côté backend (voir CategoriesService.update()).
        await updateMut.mutateAsync({
          name,
          slug: slug || undefined, // si vide: ne pas toucher
          vatRate: vatRate ?? null,
        } as any)
        toast.success('Catégorie modifiée')
      } else {
        await createMut.mutateAsync({
          name,
          slug: slug || undefined, // backend auto-slug si absent
          vatRate,
        })
        toast.success('Catégorie créée')
      }
      setOpen(false)
    } catch (e: any) {
      toast.error('Erreur', {
        description: e?.response?.data?.message ?? 'Action échouée',
      })
    }
  }

  const confirmDelete = async () => {
    if (!pendingDelete) return
    try {
      await deleteMut.mutateAsync({ id: pendingDelete.id, detach })
      toast.success('Catégorie supprimée')
      setConfirmOpen(false)
      setPendingDelete(null)
    } catch (e: any) {
      toast.error('Suppression échouée', {
        description: e?.response?.data?.message,
      })
    }
  }

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between gap-3'>
        <h3 className='text-2xl font-semibold'>Catégories</h3>
        <div className='flex items-center gap-2'>
          <Button onClick={openCreate}>Ajouter</Button>
        </div>
      </div>

      {status === 'pending' ? (
        <Loading />
      ) : (
        <>
          <div className='text-sm text-muted-foreground'>
            Total: <b>{total}</b>
          </div>
          <DataTable<Category, unknown>
            columns={columns}
            data={items}
            {...tableProps}
            total={total}
            pageCount={pageCount}
            searchValue={search}
            onSearchChange={setSearch}
            rowClickable
            onRowClick={(row) =>
              navigate(
                `/items?categoryId=${row.id}&category=${encodeURIComponent(
                  row.name ?? ''
                )}`
              )
            }
          />
          <div className='text-xs text-muted-foreground'>
            {isFetching ? 'Actualisation…' : null}
          </div>
        </>
      )}

      {/* --- Modale CRÉER / ÉDITER --- */}
      <AppDialog
        open={open}
        onOpenChange={setOpen}
        title={editing ? 'Modifier une catégorie' : 'Ajouter une catégorie'}
        description='Renseignez le nom. Le slug est optionnel (auto-généré si laissé vide).'
        asForm
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
        footer={
          <ModalActions
            onConfirm={submit}
            confirmLabel='Valider'
            confirmLoading={createMut.isPending || updateMut.isPending}
          />
        }
      >
        <div className='w-full px-4 py-2 space-y-3'>
          <div className='grid gap-1.5'>
            <Label>Nom</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder='Ex: Boissons'
            />
          </div>
          <div className='grid gap-1.5'>
            <Label>Slug (optionnel)</Label>
            <Input
              value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
              placeholder='Ex: boissons — laisser vide pour auto'
            />
          </div>
          <div className='grid gap-1.5'>
            <Label>TVA (%) — optionnel</Label>
            <Input
              inputMode='decimal'
              type='number'
              step='0.01'
              min={0}
              max={100}
              value={form.vatRate}
              onChange={(e) =>
                setForm((f) => ({ ...f, vatRate: e.target.value }))
              }
              placeholder='Ex: 18'
            />
          </div>
        </div>
      </AppDialog>

      {/* --- Modale CONFIRM SUPPRESSION --- */}
      <AppDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title='Supprimer la catégorie'
        description={
          pendingDelete?.itemsCount && pendingDelete.itemsCount > 0
            ? `Cette catégorie est utilisée par ${pendingDelete.itemsCount} article(s).`
            : 'Cette action est irréversible.'
        }
        footer={
          <ModalActions
            confirmVariant='destructive'
            confirmLabel='Supprimer'
            confirmLoading={deleteMut.isPending}
            onConfirm={confirmDelete}
          />
        }
      >
        <div className='px-4 py-2 text-sm space-y-2'>
          <div>
            Voulez-vous vraiment supprimer <b>{pendingDelete?.name}</b> ?
          </div>
          {pendingDelete?.itemsCount && pendingDelete.itemsCount > 0 ? (
            <label className='flex items-center gap-2 select-none'>
              <input
                type='checkbox'
                className='h-4 w-4'
                checked={detach}
                onChange={(e) => setDetach(e.target.checked)}
              />
              <span>Détacher les articles associés puis supprimer</span>
            </label>
          ) : null}
        </div>
      </AppDialog>
    </div>
  )
}
